// ForceUploadButton — manual trigger upload kolejki (Sprint #2, 2026-05-04 wieczór).
//
// LEKCJA #38 (incydent rano 04.05 [D-F]): Background Sync drzemiące. Android Chrome
// PWA standalone optymalizuje baterii czekając na "dobre warunki" przed sync. Internet
// OK ≠ sync odpali. UI trigger button = self-service workaround dla techników,
// eliminuje telefony do biura przy 14 zespołach.
//
// Strategia: 3 redundantne triggery na klik:
//   1. uploadManager.trigger()                    — lokalny tab natychmiast
//   2. uploadManager.requestBackgroundSync()      — register photo-sync event (Android Chrome)
//   3. SW postMessage TRIGGER_SYNC                — broadcast do innych otwartych tabów
//
// 3 fazy UI: idle → pressing (3s) → done (1s) → idle. Disabled gdy:
//   - !isOnline
//   - queueCount === 0
//   - isUploading (manager już pracuje)
//   - phase !== 'idle' (debounce klikania)

import { useState, useCallback } from 'react';
// S3 (Sprint Hygiene & Polish 2026-05-08): import z services/ (po orphan cleanup
// offline/uploadManager.js). API .trigger + .requestBackgroundSync dodane do
// services/uploadManager.js żeby zachować ForceUploadButton API bez zmian.
import { uploadManager } from '../../services/uploadManager.js';
import { useOffline } from '../../hooks/useOffline.js';
import { TOKENS } from '../../shared/tokens.js';
import { forceUpload } from './forceUpload.js';
import { useLang } from '../../hooks/useLang.js';

// Re-export dla backward-compat (testy historyczne mogą importować z .jsx).
export { forceUpload };

// R2b-d (monit 1215, 19.08 — koniec teatru): stany WYŁĄCZNIE z realnych zdarzeń.
// „✓ Wysłane" tylko gdy kolejka faktycznie zeszła do zera po przebiegu;
// cokolwiek zostało → „⚠ nie wysłano (N)". Wcześniej fazy szły na sztywnych
// timerach (3 s → „Wysłane") — incydent D.Tyrek: zielony sukces przy 9 wiszących.
// Teksty faz w i18n: force.idle / force.pressing / force.done / force.partial.

const RESULT_VISIBLE_MS = 4000;

/**
 * @param {Object} [props]
 * @param {string} [props.label] UZUPEŁNIENIE 2 (12.09): etykieta stanu spoczynku — baner FAILED
 *   pokazuje „↻ Ponów" (ten sam force: reanimacja failed → pending), baner ZATOR „📤 Wyślij teraz".
 */
export default function ForceUploadButton({ label } = {}) {
  const { isOnline, queueCount, isUploading } = useOffline();
  const { t } = useLang();
  const [phase, setPhase] = useState('idle');
  const [leftCount, setLeftCount] = useState(0); // dla fazy 'partial'

  const onClick = useCallback(async () => {
    if (phase !== 'idle') return;
    setPhase('pressing');
    let left = queueCount;
    try {
      await forceUpload({
        uploadManager,
        sw: typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
      });
      left = await uploadManager.getCount();
    } catch {
      left = await uploadManager.getCount().catch(() => queueCount);
    }
    if (left === 0) {
      setPhase('done');
    } else {
      setLeftCount(left);
      setPhase('partial');
    }
    setTimeout(() => setPhase('idle'), RESULT_VISIBLE_MS);
  }, [phase, queueCount]);

  // Widoczny gdy są zdjęcia w kolejce ALBO trwa upload (sygnał że button "działa").
  // Ukryty gdy queue empty + nic się nie dzieje (wtedy nie ma sensu).
  const visible = isOnline && (queueCount > 0 || isUploading || phase !== 'idle');
  if (!visible) return null;

  const disabled = !isOnline || queueCount === 0 || isUploading || phase !== 'idle';

  const text = phase === 'partial'
    ? t('force.partial', { n: leftCount })
    : (phase === 'idle' && label) ? label : t(`force.${phase}`);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={t('force.title')}
      style={{
        padding: '6px 12px',
        background: phase === 'done'
          ? 'rgba(34,197,94,0.25)'
          : phase === 'partial'
          ? 'rgba(var(--fo-red-rgb, 239,68,68), 0.25)'
          : phase === 'pressing'
          ? 'rgba(234,88,12,0.25)'
          : 'rgba(255,255,255,0.15)',
        border: phase === 'done'
          ? '1px solid rgba(34,197,94,0.6)'
          : phase === 'partial'
          ? '1px solid var(--fo-red, #ef4444)'
          : '1px solid rgba(255,255,255,0.3)',
        borderRadius: 6,
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        fontFamily: TOKENS.font.sans,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && phase === 'idle' ? 0.5 : 1,
        WebkitTapHighlightColor: 'transparent',
        marginLeft: 10,
        flexShrink: 0,
      }}
    >
      {text}
    </button>
  );
}
